import { Autocomplete, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { FiEdit, FiTag, FiToggleLeft, FiToggleRight } from "react-icons/fi";
import { Link } from "react-router-dom";
import {
  CompanyInterface,
  FinancialCategoryInterface
} from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import InfoBanner from "../../components/InfoBanner";
import { FinancialEntryTypeLabel } from "../../components/enum/financialEnums";
import FinancialCategoryCreateModal from "../../components/modal/FinancialCategoryCreateModal";
import { ToastError, ToastSuccess } from "../../components/toaster/toast.utils";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import {
  ActionButton,
  Container,
  FilterCard,
  Table,
  TableContainer,
  TabsBar,
  TabsRow,
  Title
} from "./financial.styled";

const FinancialCategories: React.FC = () => {
  const { isAdmin } = useAuth();

  const [categories, setCategories] = useState<FinancialCategoryInterface[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<FinancialCategoryInterface | null>(null);

  const [companies, setCompanies] = useState<CompanyInterface[]>([]);
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyInterface | null>(null);

  // ADMIN sem empresa escolhida não tem categoria para listar nem para criar.
  const waitingCompany = isAdmin && !selectedCompany;

  const fetchCategories = async () => {
    try {
      const { data } = await api.get("/financial/categories", {
        params: {
          limit: 100,
          showActives: "false",
          company_id: selectedCompany?.company_id
        }
      });
      setCategories(data.items ?? []);
    } catch (error) {
      ToastError(extractErrorMessage(error));
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/company/admin")
      .then((r) => setCompanies(r.data.items ?? r.data ?? []))
      .catch((error) => ToastError(extractErrorMessage(error)));
  }, [isAdmin]);

  useEffect(() => {
    if (waitingCompany) {
      setCategories([]);
      return;
    }
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, waitingCompany]);

  const handleOpenCreate = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (category: FinancialCategoryInterface) => {
    setSelected(category);
    setModalOpen(true);
  };

  const handleToggle = async (category: FinancialCategoryInterface) => {
    try {
      await api.patch(`/financial/categories/${category.financial_category_id}/toggle-active`);
      ToastSuccess(category.is_active ? "Categoria inativada." : "Categoria ativada.");
      fetchCategories();
    } catch (error) {
      ToastError(extractErrorMessage(error));
    }
  };

  return (
    <Container>
      <Title>Financeiro</Title>

      <TabsBar>
        <TabsRow>
          <Link to="/financial">Visão geral</Link>
          <Link to="/financial/entries">Lançamentos</Link>
          <Link to="/financial/categories" className="active">
            Categorias
          </Link>
        </TabsRow>

        <CustomTooltip
          title={waitingCompany ? "Escolha uma empresa primeiro" : ""}
        >
          {/* span porque botão desabilitado não dispara os eventos do tooltip */}
          <span>
            <CreateButton onClick={handleOpenCreate} disabled={waitingCompany}>
              <FiTag size={18} /> Criar Categoria
            </CreateButton>
          </span>
        </CustomTooltip>
      </TabsBar>

      <InfoBanner icon={<FiTag size={26} />} title="Categorias">
        As categorias usadas para classificar cada lançamento (ex.: Aluguel,
        Serviços, Vendas). Organize aqui antes de lançar em Lançamentos, para
        os relatórios saírem certos.
      </InfoBanner>

      {isAdmin && (
        <FilterCard>
          <Autocomplete
            size="small"
            options={companies}
            sx={{ minWidth: 240 }}
            getOptionLabel={(opt) => opt.company_name || ""}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            value={selectedCompany}
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                helperText={
                  selectedCompany ? undefined : "Escolha uma empresa para ver"
                }
              />
            )}
          />
        </FilterCard>
      )}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Cor</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.financial_category_id}>
                <td>{c.name}</td>
                <td>{FinancialEntryTypeLabel[c.type]}</td>
                <td>
                  <span
                    style={{
                      display: "inline-block",
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      background: c.color || "var(--border)"
                    }}
                  />
                </td>
                <td>{c.is_active ? "Ativa" : "Inativa"}</td>
                <td>
                  <CustomTooltip title="Editar">
                    <ActionButton onClick={() => handleOpenEdit(c)}>
                      <FiEdit size={18} />
                    </ActionButton>
                  </CustomTooltip>
                  <CustomTooltip title={c.is_active ? "Inativar" : "Ativar"}>
                    <ActionButton onClick={() => handleToggle(c)}>
                      {c.is_active ? (
                        <FiToggleRight size={20} color="#34d399" />
                      ) : (
                        <FiToggleLeft size={20} color="var(--text-muted)" />
                      )}
                    </ActionButton>
                  </CustomTooltip>
                </td>
              </tr>
            ))}
            {categories.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", color: "var(--text-dim)" }}>
                  {waitingCompany
                    ? "Escolha uma empresa para ver as categorias dela."
                    : "Nenhuma categoria cadastrada."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <FinancialCategoryCreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchCategories}
        category={selected}
        companyId={selectedCompany?.company_id ?? null}
      />
    </Container>
  );
};

export default FinancialCategories;
